import { useState } from 'react'
import { X, Upload, FileText, AlertCircle, CheckCircle, FileSpreadsheet } from 'lucide-react'
import { api } from '@/services/api'

interface FileImporterProps {
  classId: string
  className: string
  onClose: () => void
  onSuccess: () => void
}

export default function FileImporter({ classId, className, onClose, onSuccess }: FileImporterProps) {
  const [fileName, setFileName] = useState('')
  const [fileData, setFileData] = useState('')
  const [fileType, setFileType] = useState<'excel' | 'csv'>('csv')
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setResult(null)

    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')
    setFileType(isExcel ? 'excel' : 'csv')

    const reader = new FileReader()

    if (isExcel) {
      // Excel : lire en base64
      reader.onload = (event) => {
        try {
          const result = event.target?.result as string
          const base64 = result.split(',')[1]
          if (!base64) {
            setResult({ error: 'Erreur de lecture du fichier Excel' })
            return
          }
          setFileData(base64)
        } catch (error: any) {
          setResult({ error: 'Erreur de lecture du fichier Excel: ' + error.message })
        }
      }
      reader.onerror = () => {
        setResult({ error: 'Erreur de lecture du fichier' })
      }
      reader.readAsDataURL(file)
    } else {
      // CSV : lire en texte avec plusieurs encodages possibles
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string
          if (!content) {
            setResult({ error: 'Fichier CSV vide' })
            return
          }
          setFileData(content)
        } catch (error: any) {
          setResult({ error: 'Erreur de lecture du fichier CSV: ' + error.message })
        }
      }
      reader.onerror = () => {
        setResult({ error: 'Erreur de lecture du fichier' })
      }
      // Essayer UTF-8 d'abord, puis ISO-8859-1 si échec
      try {
        reader.readAsText(file, 'UTF-8')
      } catch {
        reader.readAsText(file, 'ISO-8859-1')
      }
    }
  }

  const handleImport = async () => {
    if (!fileData || !fileName) {
      setResult({ error: 'Veuillez sélectionner un fichier' })
      return
    }

    try {
      setLoading(true)
      setResult(null)
      const response = await api.importFile(classId, fileData, fileName, replaceExisting)
      setResult(response)
      if (response.imported > 0) {
        setTimeout(() => {
          onSuccess()
        }, 2000)
      }
    } catch (error: any) {
      setResult({ error: error.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div className="card-premium max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Import Fichier</h2>
            <p className="text-sm text-gray-500">Classe : {className}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Formats acceptés
            </h3>
            <div className="space-y-2 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <FileSpreadsheet className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Excel (.xlsx, .xls)</strong> : Colonnes Nom | Prénom | Né(e) le | Sexe
                </div>
              </div>
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>CSV (.csv)</strong> : Séparateur point-virgule (;)
                  <div className="bg-white p-2 rounded mt-1 font-mono text-xs">
                    Nom;Prénom;Né(e) le;Sexe<br />
                    DUPONT;Jean;15/03/2008;Masculin
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              📅 Formats de dates acceptés : DD/MM/YYYY, M/D/YY, YYYY-MM-DD
            </p>
          </div>

          {/* Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un fichier
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-3 w-full px-6 py-8 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <Upload className="w-8 h-8 text-gray-400" />
                <div className="text-center">
                  {fileName ? (
                    <>
                      <p className="font-semibold text-gray-700">{fileName}</p>
                      <p className="text-sm text-gray-500">
                        {fileType === 'excel' ? 'Fichier Excel' : 'Fichier CSV'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-gray-700">Cliquez pour sélectionner</p>
                      <p className="text-sm text-gray-500">.csv, .xlsx, .xls</p>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Options */}
          {fileName && (
            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
              <input
                type="checkbox"
                id="replace"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <label htmlFor="replace" className="text-sm text-orange-900 font-medium cursor-pointer">
                Remplacer les élèves existants de cette classe
              </label>
            </div>
          )}

          {/* Résultat */}
          {result && (
            <div className={`p-4 rounded-xl animate-slide-up ${
              result.error 
                ? 'bg-red-100 border border-red-200' 
                : 'bg-green-100 border border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.error ? (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold ${
                    result.error ? 'text-red-900' : 'text-green-900'
                  }`}>
                    {result.error || result.message}
                  </p>
                  {result.imported !== undefined && (
                    <p className="text-sm text-green-800 mt-1">
                      {result.imported} élève(s) importé(s) sur {result.total}
                    </p>
                  )}
                  {result.errors && result.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-sm cursor-pointer hover:underline text-red-700">
                        Voir les erreurs ({result.errors.length})
                      </summary>
                      <ul className="mt-2 text-sm space-y-1 max-h-40 overflow-y-auto">
                        {result.errors.map((err: string, idx: number) => (
                          <li key={idx} className="text-red-700">• {err}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={loading || !fileName || !fileData}
              className="btn-primary-apple flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Import en cours...
                </>
              ) : (
                'Importer'
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="btn-secondary-apple flex-1"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}